import React from 'react';
import dayjs from 'dayjs';
import DataTable, { type TableColumn } from '../common/DataTable';
import { type IChildAccount } from '../../interfaces/IChildAccount';
import ChildAccountStatus from './ChildAccountStatus';
import ReconfigureAccountButton from '../controls/ReconfigureAccountButton';
import Button from '../controls/Button';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { dateTimeFormat } from '../../utilities/constants';

interface AccountsTableProps {
  accounts: IChildAccount[];
  canUpdate: boolean;
  eventSlug?: string;
  organizationAccount?: string;
}

const AccountsTable: React.FC<AccountsTableProps> = ({ accounts, canUpdate, eventSlug, organizationAccount }) => {
  if (accounts.length === 0) {
    return null;
  }

  const columns: Array<TableColumn<IChildAccount>> = [
    {
      key: 'identifier',
      label: 'Account ID',
      width: 'w-1/4',
      render: (account) => (
        <div className="whitespace-nowrap">
          {account.identifier ? (
            <a
              href={`https://${
                organizationAccount ? organizationAccount.split(/[-.]/, 1)[0].toLowerCase() + '-' : ''
              }${account.identifier.toLowerCase()}.snowflakecomputing.com`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
              title={`Open ${account.identifier} in Snowflake`}
            >
              {account.identifier}
            </a>
          ) : (
            <span>Identifier not available for account with slug {account.slug}</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: 'w-1/6',
      render: (account) => (
        <div className="whitespace-nowrap">
          <ChildAccountStatus status={account.status} />
        </div>
      ),
    },
    {
      key: 'allocated_to',
      label: 'Assigned To',
      width: 'w-1/4',
      render: (account) => (
        <div className="truncate text-ellipsis max-w-80" title={account.allocated_to ?? 'Unassigned'}>
          {account.allocated_to ?? 'Unassigned'}
        </div>
      ),
    },
    {
      key: 'allocated_at',
      label: 'Allocated At',
      width: 'w-1/6',
      render: (account) => (
        <div className="whitespace-nowrap">
          {account.allocated_at ? dayjs(account.allocated_at).format(dateTimeFormat) : 'N/A'}
        </div>
      ),
    },
  ];

  // Add Actions column only if canUpdate is true
  if (canUpdate) {
    columns.push({
      key: 'actions',
      label: 'Actions',
      width: 'w-1/6',
      render: (account) => (
        <div className="whitespace-nowrap flex flex-col gap-1 max-w-max">
          <ReconfigureAccountButton
            account={account}
            eventSlug={eventSlug}
            intent="primary"
            className="transition-colors"
            canUpdate={canUpdate}
          />
          <Button
            url={account.latest_pipeline_url ?? undefined}
            intent="light"
            size="small"
            className="hover:bg-gray-100 transition-colors"
            disabled={!account.latest_pipeline_url}
            title={!account.latest_pipeline_url ? 'No pipeline available' : 'View latest pipeline'}
          >
            <ArrowTopRightOnSquareIcon className="h-3 w-3 mr-1" />
            <span>View Pipeline</span>
          </Button>
        </div>
      ),
    });
  }

  return (
    <div className="mt-2">
      <DataTable
        data={accounts}
        columns={columns}
        keyExtractor={(account, index) => account.id?.toString() ?? index.toString()}
        className="table-fixed"
        emptyMessage="No accounts found"
      />
    </div>
  );
};

export default AccountsTable;
